<template>
	<div class="container" id="profile_section">
		<div v-if="user_level < 90">
			<h5 class="text-danger text-center mt-5">Access Denied!</h5>
		</div>

		<div v-else>
			<div class="page-header gold mt-4 mb-2">
				<back-btn></back-btn>
				<h3 class="mb-0">
					<i class="fas fa-graduation-cap pr-1"></i>
					<small v-text="course_title"></small> (<small v-text="totalRows"></small>)
				</h3>
			</div>

			<div class="row mb-2">
				<div class="col-12 text-right">
					<button type="button" class="btn btn-sm btn-outline-secondary" @click="sectionAdd">
						<i class="fas fa-graduation-cap"></i> Add New Section
					</button>
				</div>
			</div>

			<b-modal
				id="modal-section"
				:title="modal.title"
				ref="modal-section"
				no-close-on-backdrop
				size="lg"
			>
				<b-form @submit="submitSectionForm($event)">
					<b-form-group>
						<small class="text-muted">Course ID</small>
						<b-form-input
							v-model="modal.section.course_id"
							type="text"
							required
							readonly
						></b-form-input>
					</b-form-group>
					<b-form-group>
						<small class="text-muted">Section Status</small>
						<b-form-select
							v-model="modal.section.status"
							:options="statuses"
							@change="validate(modal.section)"
						></b-form-select>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Name</small>
						<b-form-input
							v-model="modal.section.title"
							type="text"
							required
							placeholder="Enter section name"
							@change="validate(modal.section)"
						></b-form-input>
						<small class="text-danger" v-if="modal.section.title === ''">
							Enter a section name
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Sub Title</small>
						<b-form-input
							v-model="modal.section.sub_title"
							type="text"
							required
							placeholder="Enter sub title"
							@change="validate(modal.section)"
						></b-form-input>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Description</small>
						<vue-editor v-model="modal.section.description"></vue-editor>
						<small class="text-danger" v-if="modal.section.description === ''">
							Enter a section description
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Main Text</small>
						<vue-editor v-model="modal.section.main_text"></vue-editor>
						<small class="text-danger" v-if="modal.section.main_text === ''">
							Enter the main text
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted"> Media - (URL to an MP3 or Youtube video) </small>

						<div v-if="modal.section.media">
							<div
								class="pb-1"
								v-for="(media, k) in modal.section.media"
								:key="'media_' + k"
							>
								<b-form-input
									v-model="modal.section.media[k]"
									type="text"
									required
									placeholder="Media URL"
									@change="validate(modal.section)"
									class=""
								></b-form-input>
							</div>
							<small class="text-danger" v-if="modal.errors.media === ''">
								Enter a valid URL
							</small>
						</div>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Order</small>
						<b-form-input
							v-model="modal.section.sort"
							type="text"
							required
							placeholder="Sort Order"
							@change="validate(modal.section)"
						></b-form-input>
						<small class="text-danger" v-if="modal.section.type === ''">
							Enter sort order
						</small>
					</b-form-group>
				</b-form>

				<p><small class="text-danger" v-text="modal.errors.process"></small></p>

				<template #modal-footer="{ cancel }">
					<div class="w-100 row">
						<div class="col text-left">
							<b-button
								type="button"
								size="md"
								variant="success"
								:disabled="modal.submitDisabled"
								@click="submitSectionForm()"
								>Save
							</b-button>
						</div>
						<div class="col text-right">
							<b-button
								size="sm"
								@click="confirmBox()"
								name="delete"
								variant="outline-danger"
								>Delete
							</b-button>
							<b-button size="sm" variant="outline-secondary" @click="cancel('cancel')">
								cancel
							</b-button>
						</div>
					</div>
				</template>
			</b-modal>

			<div class="row" id="section_wrap">
				<div class="col-md-6 my-1">
					<b-form-group class="mb-0">
						<b-input-group size="sm">
							<b-form-input
								id="filter-input"
								v-model="filter"
								type="search"
								placeholder="Search sections"
							></b-form-input>

							<b-input-group-append>
								<b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
				</div>

				<div class="col-md-3 col-4 my-1">
					<b-form-group class="mb-0">
						<b-form-select
							id="per-page-select"
							v-model="perPage"
							:options="pageOptions"
							size="sm"
						></b-form-select>
					</b-form-group>
				</div>

				<div class="col-md-3 col-8 my-1">
					<b-pagination
						v-model="currentPage"
						:total-rows="totalRows"
						:per-page="perPage"
						align="right"
						size="sm"
						class="my-0"
					></b-pagination>
				</div>

				<div class="col-12">
					<b-table
						bordered
						responsive
						:items="sections"
						:busy="isBusy"
						:fields="fields"
						:filter="filter"
						:filter-included-fields="filterOn"
						@filtered="onFiltered"
						:current-page="currentPage"
						:per-page="perPage"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
						sort-icon-left
						class="small"
						show-empty
					>
						<template v-slot:table-busy>
							<div class="text-center my-2 p-2">
								<b-spinner class="align-middle"></b-spinner>
								<br />
								<strong>Loading...</strong>
							</div>
						</template>

						<template v-slot:cell(description)="data">
							<text-clamp text-class="text-break" :html="data.item.description" />
						</template>

						<template v-slot:cell(main_text)="data">
							<text-clamp text-class="text-break" :html="data.item.main_text" />
						</template>

						<template v-slot:cell(status)="data">
							<span v-text="arrayElmGet(statuses, data.item.status)"></span>
						</template>

						<template v-slot:cell(actions)="data">
							<b-dropdown
								size="sm"
								split
								split-variant="outline-primary"
								class="gold"
								text="Edit"
								@click="sectionEdit($event, data.item)"
							>
								<b-dropdown-item>
									<div>
										<a href="#" @click="sectionEdit($event, data.item)"> Section </a>
									</div>
								</b-dropdown-item>
								<b-dropdown-item>
									<div>
										<a href="#" @click.prevent="lessonsEdit(data.item)"> Lessons </a>
									</div>
								</b-dropdown-item>
							</b-dropdown>
						</template>
					</b-table>
				</div>

				<div class="col-12 my-1">
					<b-pagination
						v-model="currentPage"
						:total-rows="totalRows"
						:per-page="perPage"
						align="right"
						size="sm"
						class="my-0"
					></b-pagination>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from '@/axios';
import BackBtn from '@/components/BackBtn.vue';
import { VueEditor } from 'vue2-editor';
import TextClamp from '@/components/programs/TextClamp.vue';

export default {
	name: 'CoursesSectionAdmin',
	components: { BackBtn, VueEditor, TextClamp },
	props: {},
	data() {
		return {
			course_title: '',
			modal: {
				title: '',
				submitDisabled: true,
				section: {},
				errors: {
					process: '',
				},
			},

			modal_section_default: {
				id: 0,
				title: '',
				status: 1,
				course_id: this.$route.params.id,
				media: [''],
			},

			statuses: [
				{ value: 0, text: 'Closed (deleted)' },
				{ value: 1, text: 'Active' },
				{ value: 2, text: 'Pending' },
			],

			errors: {},
			sections: null,

			fields: [
				{ key: 'actions', sortable: false, label: 'Actions', class: '' },
				{ key: 'title', sortable: true, label: 'Title', class: '' },
				{ key: 'sub_title', sortable: true, label: 'Sub-title', class: '' },
				{ key: 'description', sortable: false, label: 'Description', class: '' },
				{ key: 'main_text', sortable: false, label: 'Main text', class: '' },
				{ key: 'status', sortable: true, label: 'Status', class: '' },
				{ key: 'id', sortable: true, label: 'ID', class: '' },
				{ key: 'sort', sortable: true, label: 'Order', class: '' },
			],
			isBusy: true,
			sortBy: 'sort',
			sortDesc: false,
			totalRows: 0,
			currentPage: 1,
			perPage: 50,
			pageOptions: [
				{ value: 50, text: 'Num to Show' },
				25,
				50,
				100,
				{ value: 500, text: 'All' },
			],
			filter: null,
			filterOn: [],
		};
	},
	methods: {
		arrayElmGet(obj, value) {
			const entries = Object.entries(obj);
			for (const v of entries) {
				if (parseInt(v[1].value) === parseInt(value)) {
					return v[1].text;
				}
			}
			return null;
		},
		confirmBox() {
			this.$bvModal
				.msgBoxConfirm('Are you sure you want to delete this Course Section?')
				.then((value) => {
					if (value == true) {
						this.modal.section.status = 0;
						this.submitSectionForm();
					} else {
						this.$bvModal.hide('modal-section');
					}
				})
				.catch((err) => {
					console.log(err);
				});
		},
		sectionsEdit(item) {
			this.$router.push('/course_sections/' + item.id);
		},
		lessonsEdit(item) {
			this.$router.push('/course_subsections/' + item.id);
		},

		onFiltered(filteredItems) {
			this.totalRows = filteredItems.length;
			this.currentPage = 1;
		},
		submitSectionForm(e) {
			let _this = this;
			if (e) {
				e.preventDefault();
			}

			this.validate(_this.modal.section);

			_this.modal.errors.process = 'Processing...';

			let params = new URLSearchParams();

			if (_this.modal.section) {
				const obj = Object.entries(_this.modal.section);
				for (const [k, item] of obj) {
					if (k !== 'media') {
						params.append('section[' + k + ']', item);
					}
				}
				const objM = Object.entries(_this.modal.section.media);
				for (const [k, item] of objM) {
					params.append('section[media][' + k + ']', item);
				}
			}

			axios({
				method: 'post',
				url: 'courseSectionAddEdit',
				data: params,
			})
				.then(function (response) {
					_this.modal.errors.process = response.data.message;

					if (response.data.status == 1) {
						if (response.data.section !== null) {
							if (response.data.action_type == 'add') {
								_this.sections.unshift(response.data.section);
								_this.modal.section = _this.modal_section_default;
							} else {
								if (_this.modal.section.status === 0) {
									_this.sections = _this.sections.filter(
										(item) => item.id !== _this.modal.section.id
									);
								} else {
									_this.sections.find((obj) => {
										if (obj.id === _this.modal.section.id) {
											obj = response.data.section;
										}
									});
								}
							}
						}

						_this.totalRows = _this.sections.length;

						_this.$refs['modal-section'].hide();
					} else {
						_this.modal.errors.process = response.data.message;
					}
				})
				.catch(function (e) {
					console.log('Error: submitSectionForm:' + e);
				});
		},
		sectionAdd() {
			this.modal.section = this.modal_section_default;
			if (!this.modal.section.title) {
				this.modal.title = 'Add Section';
				this.modal.errors.process = '';
			}
			this.$bvModal.show('modal-section');
			this.validate(this.modal.section);
		},
		sectionEdit(e, item) {
			this.modal.title = 'Edit Section';
			this.modal.errors.process = '';
			this.modal.section = item;
			this.$bvModal.show('modal-section');
			this.validate(this.modal.section);
		},
		courseSectionsGet() {
			let _this = this;
			let params = new URLSearchParams();
			params.append('id', this.$route.params.id);
			axios({
				url: 'courseSectionsGet',
				params: params,
			})
				.then(function (response) {
					_this.isBusy = false;
					if (response.data.status == 1) {
						_this.sections = Object.values(response.data.sections);
						_this.course_title = response.data.title;
					} else {
						_this.sections = null;
						_this.course_title = '';
					}
					_this.totalRows = _this.sections.length;
				})
				.catch(function () {
					console.log('Error: coursesSectionsGet.');
				});
		},

		validate(item) {
			var valid = true;
			if (item.status === '' || item.status === 'null') {
				valid = false;
			}
			if (item.title === '') {
				valid = false;
			}

			if (valid == true) {
				this.modal.submitDisabled = false;
				return false;
			} else {
				this.modal.submitDisabled = true;
				return true;
			}
		},
	},
	computed: {
		sortOptions() {
			return this.fields
				.filter((f) => f.sortable)
				.map((f) => {
					return { text: f.label, value: f.key };
				});
		},
		active_profile() {
			return this.$store.getters.profileGet;
		},
		user_level() {
			return this.active_profile.level;
		},
	},
	created() {},
	mounted() {
		this.courseSectionsGet();
	},
	watch: {},
};
</script>

<style scoped>
.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	position: relative;
	padding: 0 52px;
}
.page-header h3 {
	min-width: 0;
	text-align: center;
	word-break: break-word;
	font-size: clamp(1rem, 4vw, 1.75rem);
}

</style>
